/* eslint-disable react/jsx-indent */
import React, { useEffect, useState, Suspense, lazy } from "react";
import {
  BrowserRouter,
  Route,
  Switch,
  useHistory,
  useLocation,
} from "react-router-dom";
import { getCurrentNotificationsAsync } from "./store/notificationSlice";
// import { getTrainingRecordsAsync } from "./store/trainingSlice";
import { useAppDispatch, useAppSelector } from "./store/hooks";
import { getCurrentUserAsync } from "./store/userSlice";
import { getBillboardAssignmentsAsync } from "./store/billboardAssignmentSlice";
import { getCurrentTodoAsync } from "./store/todoSlice";
import { getCurrentPostArticleAssignmentsAsync } from "./store/postArticleAssignmentSlice";
import { getCurrentQuestionnaireAssignmentsAsync } from "./store/questionnaireAssignmentsSlice";
import { getAllClaimsAsync } from "./store/irClaimSlice";
import LoadingModal from "./components/shared/loadingModal";

// Lazy load views
const ContentCreation = lazy(
  () => import("./components/views/ContentCreation")
);
const CyberThreatAlerts = lazy(
  () => import("./components/views/CyberThreatAlerts")
);
const Dashboard = lazy(() => import("./components/views/Dashboard"));
const HowTo = lazy(() => import("./components/views/HowTo"));
const Members = lazy(() => import("./components/views/Members"));
const NotificationsList = lazy(
  () => import("./components/views/Notifications")
);
const PageNotFound = lazy(() => import("./components/views/PageNotFound"));
const Profile = lazy(() => import("./components/views/Profile"));
const Questionnaires = lazy(() => import("./components/views/Questionnaires"));
const Trainings = lazy(() => import("./components/views/Training"));
const Users = lazy(() => import("./components/views/Users"));
const CyberClaims = lazy(() => import("./components/views/CyberClaims"));
const PostsAndArticles = lazy(
  () => import("./components/views/PostsAndArticles")
);
const Documents = lazy(() => import("./components/views/Documents"));
const Checklists = lazy(() => import("./components/views/Checklists"));
const NoAccessPermitted = lazy(
  () => import("./components/views/NoAccessPermitted")
);

interface Props {}

// ScrollToTop component to reset scroll position on route change
export const ScrollToTop: React.FC = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
};

const AppRouter: React.FC<Props> = () => {
  const dispatch = useAppDispatch();
  const history = useHistory();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null); // State to capture error messages

  // Fetch required data when app loads
  // const getTrainingThenNotificationsAsync = async () => {
  //   await dispatch(getCurrentNotificationsAsync({}));
  //   await dispatch(getTrainingRecordsAsync());
  //   await dispatch(getCurrentQuestionnaireAssignmentsAsync());
  // };
  const getSort = () => [`sortDesc_irClaim.employerReportDate`].join(",");

  useEffect(() => {
    const fetchData = async () => {
      try {
        await dispatch(getCurrentUserAsync()).unwrap();
        // await getTrainingThenNotificationsAsync();
        // await dispatch(getBillboardAssignmentsAsync());
        // await dispatch(getCurrentPostArticleAssignmentsAsync());
        // await dispatch(getCurrentTodoAsync());
        // await dispatch(
        //   getAllClaimsAsync({
        //     pageIndex: 1,
        //     pageSize: 10,
        //     hideclosedClaims: true,
        //     myClaims: false,
        //     orderBy: getSort(),
        //   })
        // );
        // Run other API calls in parallel
        await Promise.all([
          // dispatch(getCurrentTodoAsync()),
          dispatch(getCurrentNotificationsAsync({})),
          // dispatch(getTrainingRecordsAsync()),
          // dispatch(getCurrentQuestionnaireAssignmentsAsync()),
          dispatch(getBillboardAssignmentsAsync()),
          dispatch(
            getAllClaimsAsync({
              pageIndex: 1,
              pageSize: 10,
              hideclosedClaims: true,
              myClaims: false,
              orderBy: [`sortDesc_irClaim.employerReportDate`].join(","),
              type: "dashboard",
            })
          ),
        ]);

        // Fetch `getCurrentTodoAsync` separately
        // await dispatch(getCurrentTodoAsync());
        setLoading(false);
      } catch (err: any) {
        setLoading(false);
        if (err && err?.errorFrom === "user") {
          localStorage.removeItem("refreshToken");
          localStorage.removeItem("accessToken");
          localStorage.removeItem("email");
          window.location.href = "/";
        }
      }
    };
    fetchData();
  }, [dispatch]);

  const currentState = useAppSelector((state) => ({
    user: state.user,
  }));

  const superAdminRole =
    process.env.REACT_APP_PRIMEX_SUPER_ADMIN_ROLE_ACCESS_LEVEL &&
    parseInt(process.env.REACT_APP_PRIMEX_SUPER_ADMIN_ROLE_ACCESS_LEVEL, 10);

  const { currentUser } = currentState.user;

  // Show error message if there's an error
  // if (error) {
  //   return (
  //     <div className="error-message">
  //       <h2>Error</h2>
  //       <p>{error}</p>
  //     </div>
  //   );
  // }

  // Show loading indicator if data is still loading
  if (loading) {
    return <LoadingModal show={loading} />;
  }

  return (
    <>
      {currentUser && !currentUser.cyberNone ? (
        <Suspense fallback={<LoadingModal show={true} />}>
          <BrowserRouter>
            <ScrollToTop />
            <Switch>
              <Route exact path="/" component={Dashboard} />
              <Route exact path="/profile" component={Profile} />
              <Route path="/questionnaires" component={Questionnaires} />
              <Route path="/threat-alerts" component={CyberThreatAlerts} />
              <Route path={["/member", "/members"]} component={Members} />
              <Route path="/users" component={Users} />
              <Route path="/training" component={Trainings} />
              <Route path="/how-to" component={HowTo} />
              <Route path="/notifications" component={NotificationsList} />
              <Route path="/posts-articles" component={PostsAndArticles} />
              <Route path="/checklists" component={Checklists} />
              <Route path="/documents" component={Documents} />
              <Route path="/cyber-claims" component={CyberClaims} />
              {currentUser &&
              currentUser.role &&
              superAdminRole &&
              currentUser.role >= superAdminRole ? (
                <Route path="/content" component={ContentCreation} />
              ) : null}
              <Route component={PageNotFound} />
            </Switch>
          </BrowserRouter>
        </Suspense>
      ) : null}
      {currentUser && currentUser.cyberNone && <NoAccessPermitted />}
    </>
  );
};

export default AppRouter;
